/**
 * Asia/Kathmandu (UTC+05:45, no DST) for playback search UI.
 * Must match JT808 BCD device-local time on the gateway.
 */
export const PLAYBACK_TIME_ZONE = "Asia/Kathmandu";
export const KATHMANDU_OFFSET_MS = (5 * 60 + 45) * 60 * 1000;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Device wall-clock → UTC ms. */
export function kathmanduWallToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0,
  ms = 0,
): number {
  return Date.UTC(year, month - 1, day, hour, minute, second, ms) - KATHMANDU_OFFSET_MS;
}

/** UTC ms → Kathmandu wall parts. */
export function utcMsToKathmanduWall(ms: number): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const shifted = new Date(ms + KATHMANDU_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
  };
}

/**
 * Shift server now onto the terminal clock.
 * skewMs is serverTime − deviceTime (positive when the camera is slow).
 * A typed OSD time must not pass through this — only a default "now".
 */
export function deviceClockMs(now = Date.now(), skewMs?: number | null): number {
  if (skewMs == null || !Number.isFinite(skewMs)) return now;
  if (Math.abs(skewMs) > 6 * 60 * 60 * 1000) return now;
  return now - skewMs;
}

export function kathmanduDateInputValue(ms = Date.now()): string {
  const w = utcMsToKathmanduWall(ms);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
}

export function kathmanduTimeInputValue(ms = Date.now()): string {
  const w = utcMsToKathmanduWall(ms);
  return `${pad(w.hour)}:${pad(w.minute)}`;
}

/** Parse UI date+time as Kathmandu wall clock → UTC ISO bounds. */
export function rangeFromKathmandu(
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
): { from: string; to: string; date: string } | { error: string } {
  const parse = (date: string, time: string, endOfMinute: boolean): number => {
    const [y, m, d] = date.split("-").map(Number);
    const parts = (time || "00:00").split(":").map((part) => Number(part) || 0);
    const hh = parts[0] ?? 0;
    const mm = parts[1] ?? 0;
    const ss = parts.length >= 3 ? (parts[2] ?? 0) : endOfMinute ? 59 : 0;
    const ms = endOfMinute ? 999 : 0;
    if (!y || !m || !d) return Number.NaN;
    return kathmanduWallToUtcMs(y, m, d, hh, mm, ss, ms);
  };
  const startMs = parse(startDate, startTime, false);
  const endMs = parse(endDate, endTime, true);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return { error: "Enter a valid start and end date/time." };
  }
  if (!(endMs > startMs)) {
    return { error: "End time must be after start time." };
  }
  const MAX_QUERY_MS = 7 * 24 * 60 * 60 * 1000;
  if (endMs - startMs > MAX_QUERY_MS) {
    return { error: "Choose a range of 7 days or less so the camera can list files on its storage." };
  }
  return {
    date: startDate,
    from: new Date(startMs).toISOString(),
    to: new Date(endMs).toISOString(),
  };
}

/** Clock only, always Asia/Kathmandu, independent of the browser zone. */
export function formatKathmanduClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString("en-US", {
    timeZone: PLAYBACK_TIME_ZONE,
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

/** Positive driftMs means the terminal clock is behind the server. */
export function formatClockDrift(driftMs: number): string {
  const behind = driftMs > 0;
  const sec = Math.round(Math.abs(driftMs) / 1000);
  const label = sec < 90 ? `${sec}s` : `${Math.round(sec / 60)}m`;
  return behind ? `${label} behind` : `${label} ahead`;
}

export function formatKathmanduWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    timeZone: PLAYBACK_TIME_ZONE,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

export function formatKathmanduDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    timeZone: PLAYBACK_TIME_ZONE,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
